import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useFlags } from 'launchdarkly-react-client-sdk';

import {
	CloseWithCircle,
	RuAdditionalButton,
} from '@silverrailtech/srtlibrary';

import messages from '../../../messagesCustomerAccounts';
import PersonalInfo from '../PersonalInfo/PersonalInfo';
import RailCards from '../RailCards/RailCards';
import PhotoCard from '../PhotoCard/PhotoCard';
import Bookings from '../Bookings/Bookings';
import { availableRailOperators } from '../../mockData';
import {
	AccountDetailsWrapper,
	BookingHeader,
	FlexWrapper,
} from '../../ManageCustomerAccountStyled';
import EVouchers from '../EVouchers/EVouchers';
import SmartCards from '../SmartCards/SmartCards';

import gwrLogo from '../../../../images/customerLogos/gwrLogo.svg';
import hullLogo from '../../../../images/customerLogos/hullLogo.svg';
import lumoLogo from '../../../../images/customerLogos/lumoLogo.jpeg';
import swrLogo from '../../../../images/customerLogos/swrLogo.svg';
import scotrailLogo from '../../../../images/customerLogos/scotrailLogo.svg';
import saLogo from '../../../../images/logoSA.svg';
import ResendEmail from '../ResendEmail/ResendEmail';
import { getCustomerAccountData } from '../../../../api';
import { clearCustomerAccountData } from '../../../actionsCustomerAccount';
import useAuth from '../../../../hooks/useAuth';
import { setCustomersActiveRealm } from '../../../../actions/authActions';
import { LOCAL_STORAGE_KEYS } from '../../../../constants/constants';

const CloseCustomerDetailButton = ({ intl: { formatMessage } }) => {
	const dispatch = useDispatch();

	return (
		<RuAdditionalButton
			variant="outlined"
			color="swap"
			endIcon={<CloseWithCircle width="16px" height="16px" />}
			onClick={() => {
				localStorage.removeItem(LOCAL_STORAGE_KEYS.customerAccountToken);
				dispatch(clearCustomerAccountData());
				dispatch(setCustomersActiveRealm(null));
			}}
			data-testid="closeCustomerDetailButton"
			aria-label={formatMessage(messages.lblDetailsCloseButton)}
		>
			<FormattedMessage {...messages.lblDetailsCloseButton} />
		</RuAdditionalButton>
	);
};

CloseCustomerDetailButton.propTypes = {
	intl: PropTypes.object.isRequired,
};

const IntlCloseCustomerDetailButton = injectIntl(CloseCustomerDetailButton);

const AccountDetails = () => {
	const { saResetPasswordClientFlow } = useFlags();
	const { getAuthHeaders, accountId } = useAuth();

	const [personalInfo, setPersonalInfo] = useState(null);
	const [isPersonalDataLoading, setPersonalDataLoading] = useState(true);
	const [error, setError] = useState('');

	useEffect(() => {
		if (accountId) {
			getCustomerAccountData(
				null,
				getAuthHeaders(),
				`/${accountId}`,
				(err) => {
					// handle what we show if api fails in component library
					// - we do not have backup/design if no formData or handle here with UI
					setError(err.message || 'Failed to load Personal Info');
					setPersonalDataLoading(false);
				},
				(data) => {
					setPersonalInfo(data);
					setPersonalDataLoading(false);
				},
			);
		}
	}, [accountId, getAuthHeaders]);

	const customerLogo = useMemo(() => {
		const headers = getAuthHeaders();
		switch (headers?.['X-Tenant-Id']) {
			case 'gwr':
				return { alt: 'gwr-logo', img: gwrLogo };
			case 'hull':
				return { alt: 'hull-logo', img: hullLogo };
			case 'lumo':
				return { alt: 'lumo-logo', img: lumoLogo };
			case 'scotrail':
				return { alt: 'scotrail-logo', img: scotrailLogo };
			case 'swr':
				return { alt: 'swr-logo', img: swrLogo };
			default:
				return { alt: 'silver-agent-logo', img: saLogo };
		}
	}, [getAuthHeaders]);

	return (
		<AccountDetailsWrapper>
			<div className="container">
				<div className="row">
					<div className="col-12">
						{availableRailOperators.length > 1 && (
							<BookingHeader>
								<FlexWrapper>
									<img
										alt={customerLogo.alt}
										src={customerLogo.img}
										height={50}
										loading="lazy"
									/>
									<IntlCloseCustomerDetailButton />
								</FlexWrapper>
							</BookingHeader>
						)}
						<FlexWrapper>
							<h1>
								<FormattedMessage {...messages.lblDetailsTitle} />
							</h1>
							{availableRailOperators.length === 1 && (
								<IntlCloseCustomerDetailButton />
							)}
						</FlexWrapper>
					</div>
				</div>
				<div className="row">
					<div className="col-12 col-lg-6 mb-3">
						<div className="mb-3">
							<PersonalInfo
								personalInfo={personalInfo}
								personalInfoError={error}
								setError={setError}
								isPersonalDataLoading={isPersonalDataLoading}
								isAgent
							/>
						</div>
						{saResetPasswordClientFlow && <ResendEmail />}
					</div>
					<div className="col-12 col-lg-6">
						<div className="mb-3">
							<RailCards />
						</div>
						<PhotoCard />
					</div>
				</div>
			</div>
			<Bookings />
			<SmartCards addresses={personalInfo?.addresses || []} />
			<EVouchers />
		</AccountDetailsWrapper>
	);
};

export default AccountDetails;
